import { NgClass, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, computed, inject, output, signal } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { ColumnMappingRow, ImportTargetColumn, ImportWizardStep } from '@app/v2/shared/types/import-target-column.type'
import {
  DEFAULT_IMPORT_TARGET_COLUMNS,
  buildAutoColumnMappings,
  buildImportDataMapping,
  countMappedColumns,
  hasRequiredContactFieldMapped
} from '@app/v2/shared/utils/csv-column-mapper.util'
import { parseCsvText } from '@app/v2/shared/utils/csv-import.parser.util'
import { areImportTargetColumnsEqual } from '@app/v2/shared/utils/import-target-columns.util'

@Component({
  selector: 'app-import-modal',
  standalone: true,
  imports: [ModalComponent, ReactiveFormsModule, NgClass, NgTemplateOutlet],
  templateUrl: './import-modal.component.html',
  styleUrl: './import-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportModalComponent implements OnChanges {
  @Input() id = 'import-data-modal'
  @Input() header = 'Import Data'
  @Input() targetColumns: ImportTargetColumn[] = []

  readonly downloadTemplate = output<void>()
  readonly fileChange = output<File>()
  readonly importRequested = output<{ file: File; dataMapping: Record<string, string> }>()
  readonly closed = output<void>()

  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>

  public readonly supportedFormats = '.csv'
  public readonly skipFieldOption = '-- Skip this field --'
  public readonly wizardSteps: ImportWizardStep[] = [
    { number: 1, name: 'Select File' },
    { number: 2, name: 'Map Columns' },
    { number: 3, name: 'Review & Import' }
  ]

  public readonly currentStep = signal(1)
  public readonly isDragging = signal(false)
  public readonly isParsingFile = signal(false)
  public readonly isDuplicateHeaders = signal(false)
  public readonly isImporting = signal(false)
  public readonly effectiveTargetColumns = signal<ImportTargetColumn[]>(DEFAULT_IMPORT_TARGET_COLUMNS)
  public readonly columnMappingRows = signal<ColumnMappingRow[]>([])
  public readonly mappedColumnsCount = signal(0)
  public readonly recordsToImport = signal(0)
  public readonly file = signal<File | null>(null)
  public readonly fileColumns = signal<string[]>([])
  public readonly fileRows = signal<Record<string, string>[]>([])
  public readonly invalidFileType = signal(false)
  public readonly requiredContactMapped = signal(false)
  public readonly fileSizeLabel = signal('')
  public mappingForm = new FormGroup({})

  public fileForm = new FormGroup({
    fileInput: new FormControl<File | null>(null, Validators.required)
  })

  public readonly isNextDisabled = computed(() => {
    if (this.currentStep() === 1) {
      return !this.file() || this.isParsingFile() || this.fileRows().length === 0 || this.isDuplicateHeaders() || this.invalidFileType()
    }

    if (this.currentStep() === 2) {
      return !this.requiredContactMapped()
    }

    return false
  })

  public readonly canImportProceed = computed(() => {
    return this.mappedColumnsCount() > 0 && !!this.file() && this.requiredContactMapped()
  })

  private readonly _modalService = inject(ModalService)
  private fileData: Record<string, string>[] = []
  private fileReadSequence = 0

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['targetColumns']) return

    const previousRaw = changes['targetColumns'].previousValue as ImportTargetColumn[] | undefined
    const previousColumns = this._resolvedColumns(previousRaw)
    const nextColumns = this._resolveEffectiveTargetColumns()

    if (previousRaw !== undefined && areImportTargetColumnsEqual(previousColumns, nextColumns)) return

    this.effectiveTargetColumns.set(nextColumns)

    if (this.fileColumns().length > 0) {
      this._initColumnMappings()
      this._applyAutoColumnMappings()
    }
  }

  /**
   * Checks if the modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isOpen(): boolean {
    return this._modalService.isModalOpen(this.id)
  }

  /**
   * Opens the modal.
   * @returns void
   */
  public open(): void {
    if (!this.isOpen()) this._modalService.toggleModal(this.id)
  }

  /**
   * Closes the modal.
   * @returns void
   */
  public close(): void {
    this._closeModal(true)
  }

  /**
   * Handles the close modal event.
   */
  public onCloseModal(): void {
    this._closeModal(false)
  }

  /**
   * Handles the drag over event.
   * @param event {DragEvent} - The drag event.
   * @returns void
   */
  public onDragOver(event: DragEvent): void {
    this._preventDragDefault(event)
    this.isDragging.set(true)
  }

  /**
   * Handles the drag leave event.
   */
  public onDragLeave(event: DragEvent): void {
    this._preventDragDefault(event)
    this.isDragging.set(false)
  }

  /**
   * Handles the drop event.
   */
  public onDrop(event: DragEvent): void {
    this._preventDragDefault(event)
    this.isDragging.set(false)

    const droppedFile = event.dataTransfer?.files?.[0]
    if (!droppedFile) return

    this._clearFileInputValue()
    this._processSelectedFile(droppedFile)
  }

  /**
   * Handles the file change event.
   * @param files {FileList | null} - The file list.
   * @returns void
   */
  public onFileChange(files: FileList | null): void {
    const selectedFile = files?.[0]
    if (!selectedFile) {
      this._setInvalidFileType(true)
      this.file.set(null)
      return
    }

    this._processSelectedFile(selectedFile)
  }

  /**
   * Handles the remove file event.
   * @param event {MouseEvent} - The mouse event.
   * @returns void
   */
  public removeFile(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this._resetFileState()
  }

  /**
   * Handles the download template click event.
   * @returns void
   */
  public downloadTemplateClick(): void {
    this.downloadTemplate.emit()
  }

  /**
   * Handles the go to next step event.
   * @returns void
   */
  public goToNextStep(): void {
    if (this.isNextDisabled()) return

    if (this.currentStep() === 2) {
      this.mapData()
      return
    }

    if (this.currentStep() < this.wizardSteps.length) {
      this.currentStep.update((step) => step + 1)
    }
  }

  /**
   * Handles the go to previous step event.
   * @returns void
   */
  public goToPreviousStep(): void {
    if (this.currentStep() <= 1 || this.isImporting()) return
    this.currentStep.update((step) => step - 1)
  }

  /**
   * Checks if the required contact field is mapped.
   * @returns Boolean indicating if the required contact field is mapped.
   */
  public hasRequiredContactFieldMapped(): boolean {
    return this.requiredContactMapped()
  }

  /**
   * Updates the mapping for a source column.
   * @param sourceColumn {string} - The source column.
   * @param targetColumn {string} - The target column.
   * @returns void
   */
  public updateMapping(sourceColumn: string, targetColumn: string): void {
    const sanitizedSource = sourceColumn.trim()

    if (sanitizedSource) {
      this.effectiveTargetColumns().forEach((column) => {
        if (column.field === targetColumn) return

        const control = this.mappingForm.get(column.field)
        if (control?.value === sanitizedSource) {
          control.setValue('')
        }
      })
    }

    const control = this.mappingForm.get(targetColumn)
    if (!control) return

    control.setValue(sanitizedSource)
    control.markAsTouched()
    control.markAsDirty()
    this._syncMappingDerivedState()
  }

  /**
   * Checks if there are no mapped columns.
   * @returns Boolean indicating if there are no mapped columns.
   */
  public hasNoMappedColumns(): boolean {
    return this.mappedColumnsCount() === 0
  }

  /**
   * Imports the data.
   */
  public importData(): void {
    const selectedFile = this.file()
    if (!this.canImportProceed() || !selectedFile || this.isImporting()) return

    this.isImporting.set(true)
    this.importRequested.emit({
      file: selectedFile,
      dataMapping: buildImportDataMapping(this._getMappings())
    })
  }

  /**
   * Completes the import.
   * @returns void
   */
  public completeImport(): void {
    this._setImporting(false)
  }

  /**
   * Fails the import.
   * @returns void
   */
  public failImport(): void {
    this._setImporting(false)
  }

  /**
   * Maps the data.
   * @returns void
   */
  public mapData(): void {
    const mappings = this._getMappings()
    this.recordsToImport.set(this.fileData.length)
    this._refreshReviewSummary(mappings)
    this.currentStep.update((step) => step + 1)
  }

  /**
   * Checks if the file type is invalid.
   * @returns Boolean indicating if the file type is invalid.
   */
  public hasInvalidFileType(): boolean {
    return this.invalidFileType()
  }

  /**
   * Gets the mapping control for a field.
   * @param field {string} - The field.
   * @returns The mapping control.
   */
  public getMappingControl(field: string): FormControl<string> {
    return this.mappingForm.get(field) as FormControl<string>
  }

  /**
   * Processes the selected file.
   * @param selectedFile {File} - The selected file.
   * @returns void
   */
  private _processSelectedFile(selectedFile: File): void {
    if (!this._isFileTypeSupported(selectedFile)) {
      this._setInvalidFileType(true)
      this.file.set(null)
      this._resetParsedFileData()
      return
    }

    this._setInvalidFileType(false)
    this._handleFileSelection(selectedFile)
  }

  /**
   * Handles the file selection.
   * @param selectedFile {File} - The selected file.
   * @returns void
   */
  private _handleFileSelection(selectedFile: File): void {
    this.file.set(selectedFile)
    this.fileSizeLabel.set(this._formatFileSize(selectedFile.size))
    this.fileForm.get('fileInput')?.setValue(selectedFile)
    this.fileForm.get('fileInput')?.updateValueAndValidity()
    this._readFileData(selectedFile)
    this.fileChange.emit(selectedFile)
  }

  /**
   * Reads the file data.
   * @param selectedFile {File} - The selected file.
   * @returns void
   */
  private _readFileData(selectedFile: File): void {
    const readSequence = ++this.fileReadSequence
    this.isParsingFile.set(true)
    this._resetParsedFileData()

    const reader = new FileReader()

    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (readSequence !== this.fileReadSequence) return

      this.isParsingFile.set(false)

      const result = event.target?.result
      if (!result) return

      try {
        this._parseFileData(result)
      } catch {
        this._resetParsedFileData()
      }
    }

    reader.onerror = () => {
      if (readSequence !== this.fileReadSequence) return

      this.isParsingFile.set(false)
      this._resetParsedFileData()
    }

    reader.readAsArrayBuffer(selectedFile)
  }

  /**
   * Parses the file data.
   * @param data {ArrayBuffer | string} - The file data.
   * @returns void
   */
  private _parseFileData(data: ArrayBuffer | string): void {
    const csvText = typeof data === 'string' ? data : new TextDecoder().decode(data)
    const parsed = parseCsvText(csvText)

    if (parsed.columns.length === 0) {
      this._resetParsedFileData()
      return
    }

    this.isDuplicateHeaders.set(parsed.hasDuplicateHeaders)
    this.fileColumns.set(parsed.columns)
    this.fileData = parsed.rows
    this.fileRows.set(this.fileData.slice(0, 3))
    this._initColumnMappings()
    this._applyAutoColumnMappings()
  }

  /**
   * Initializes the column mappings.
   * @returns void
   */
  private _initColumnMappings(): void {
    const mappingControls: Record<string, FormControl<string>> = {}

    this.effectiveTargetColumns().forEach((targetColumn) => {
      mappingControls[targetColumn.field] = new FormControl('', { nonNullable: true })
    })

    this.mappingForm = new FormGroup(mappingControls)
    this._syncMappingDerivedState()
  }

  /**
   * Applies the auto column mappings.
   * @returns void
   */
  private _applyAutoColumnMappings(): void {
    const autoMappings = buildAutoColumnMappings(this.fileColumns(), this.effectiveTargetColumns())

    Object.entries(autoMappings).forEach(([targetField, sourceColumn]) => {
      const control = this.mappingForm.get(targetField)
      if (!control) return

      control.setValue(sourceColumn)
      control.markAsTouched()
      control.markAsDirty()
    })

    this._syncMappingDerivedState()
  }

  /**
   * Gets the mappings.
   * @returns The mappings.
   */
  private _getMappings(): Record<string, string> {
    return this.mappingForm.getRawValue() as Record<string, string>
  }

  private _refreshReviewSummary(mappings: Record<string, string>): void {
    this.mappedColumnsCount.set(countMappedColumns(mappings))
    this.columnMappingRows.set(
      this.effectiveTargetColumns().map((column) => ({
        targetColumn: column.field,
        targetHeader: column.header,
        sourceColumn: mappings[column.field]?.trim() ?? ''
      }))
    )
  }

  /**
   * Syncs the mapping derived state.
   * @returns void
   */
  private _syncMappingDerivedState(): void {
    const mappings = this._getMappings()
    this.requiredContactMapped.set(hasRequiredContactFieldMapped(this.effectiveTargetColumns(), mappings))
    this.mappedColumnsCount.set(countMappedColumns(mappings))
  }

  /**
   * Resolves the effective target columns.
   * @param columns {ImportTargetColumn[] | undefined} - The columns.
   * @returns The effective target columns.
   */
  private _resolvedColumns(columns?: ImportTargetColumn[]): ImportTargetColumn[] {
    return columns && columns.length > 0 ? columns : DEFAULT_IMPORT_TARGET_COLUMNS
  }

  /**
   * Resolves the effective target columns.
   */
  private _resolveEffectiveTargetColumns(): ImportTargetColumn[] {
    return this._resolvedColumns(this.targetColumns)
  }

  /**
   * Checks if the file type is supported.
   * @param selectedFile {File} - The selected file.
   * @returns Boolean indicating if the file type is supported.
   */
  private _isFileTypeSupported(selectedFile: File): boolean {
    const fileName = selectedFile.name.toLowerCase()
    return this.supportedFormats
      .split(',')
      .map((format) => format.trim().toLowerCase())
      .some((format) => fileName.endsWith(format))
  }

  /**
   * Sets the invalid file type.
   * @param invalid {boolean} - The invalid file type.
   * @returns void
   */
  private _setInvalidFileType(invalid: boolean): void {
    const control = this.fileForm.get('fileInput')
    if (!control) return

    if (invalid) {
      control.setErrors({ invalidFileType: true })
      this.invalidFileType.set(true)
    } else {
      control.setErrors(null)
      this.invalidFileType.set(false)
    }
  }

  /**
   * Sets the importing state.
   * @param isImporting {boolean} - The importing state.
   * @returns void
   */
  private _setImporting(isImporting: boolean): void {
    this.isImporting.set(isImporting)
  }

  /**
   * Closes the modal.
   * @param toggleModal {boolean} - The toggle modal state.
   * @returns void
   */
  private _closeModal(toggleModal: boolean): void {
    if (this.isImporting()) return
    if (toggleModal && this.isOpen()) this._modalService.toggleModal(this.id)
    this._reset()
    this.closed.emit()
  }

  /**
   * Prevents the default drag event.
   * @param event {DragEvent} - The drag event.
   * @returns void
   */
  private _preventDragDefault(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Formats the file size.
   * @param bytes {number} - The bytes.
   * @returns The formatted file size.
   */
  private _formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'

    const bytesPerUnit = 1024
    const sizeUnits = ['Bytes', 'KB', 'MB', 'GB']
    const unitIndex = Math.floor(Math.log(bytes) / Math.log(bytesPerUnit))

    return `${parseFloat((bytes / Math.pow(bytesPerUnit, unitIndex)).toFixed(2))} ${sizeUnits[unitIndex]}`
  }

  /**
   * Resets the import modal.
   * @returns void
   */
  private _reset(): void {
    this.fileReadSequence++
    this.currentStep.set(1)
    this.isImporting.set(false)
    this.isParsingFile.set(false)
    this._resetFileState()
    this._resetMappingState()
  }

  /**
   * Resets the file state.
   * @returns void
   */
  private _resetFileState(): void {
    this.file.set(null)
    this.fileSizeLabel.set('')
    this._resetParsedFileData()
    this.fileForm.reset()
    this._clearFileInputValue()
  }

  /**
   * Resets the mapping state.
   */
  private _resetMappingState(): void {
    this.recordsToImport.set(0)
    this.mappedColumnsCount.set(0)
    this.columnMappingRows.set([])
    this.requiredContactMapped.set(false)
    this.mappingForm = new FormGroup({})
  }

  /**
   * Resets the parsed file data.
   * @returns void
   */
  private _resetParsedFileData(): void {
    this.fileData = []
    this.fileColumns.set([])
    this.fileRows.set([])
    this.isDuplicateHeaders.set(false)
  }

  /**
   * Clears the file input value.
   * @returns void
   */
  private _clearFileInputValue(): void {
    if (this.fileInputRef?.nativeElement) this.fileInputRef.nativeElement.value = ''
  }
}
